import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { environment } from 'environments/environment';

declare global {
    interface Window {
        dataLayer: unknown[];
        gtag: (...args: unknown[]) => void;
        /** Set by the inline script in index.html, next to the official gtag snippet. */
        __spfInitialPagePath?: string;
        /** Host allowlist result from index.html — false on UAT and localhost. */
        __spfAnalyticsEnabled?: boolean;
    }
}

export const GA_MEASUREMENT_ID = 'G-V43Y7NZQ20';

/**
 * Service for Google Analytics (gtag) event tracking.
 *
 * The tag itself is installed with the official gtag.js snippet in index.html so
 * Tag Assistant and Google's own validators see a complete, unmodified install in
 * the HTML source. This service only rides on top of it: it reports SPA route
 * changes as `page_view` events (Google's documented single-page-app pattern) and
 * sends the custom events raised by components.
 *
 * No-ops on server for SSR compatibility.
 * Respects environment.analytics.enabled and environment.analytics.googleAnalytics.enabled flags.
 */
@Injectable({
    providedIn: 'root'
})
export class GtagService {
    private _platformId = inject(PLATFORM_ID);
    private _document = inject(DOCUMENT);

    private _initialised = false;
    private _initialPageViewHandled = false;

    init(): void {
        this.ensureTag();
    }

    /**
     * Make sure gtag() is available before we push anything at it.
     *
     * Normally the snippet in index.html has already defined it. If something
     * stripped the snippet (proxy, aggressive CDN minifier) we fall back to
     * loading gtag.js ourselves so tracking degrades instead of disappearing.
     *
     * @returns true when it is safe to call window.gtag()
     */
    private ensureTag(): boolean {
        if (!environment.analytics?.enabled || !environment.analytics?.googleAnalytics?.enabled) {
            return false;
        }

        if (!isPlatformBrowser(this._platformId)) {
            return false;
        }

        // index.html decides whether this host reports at all. Without this the
        // environment flag alone would be the only thing keeping UAT out of the
        // client's property, and the two can drift.
        if (window.__spfAnalyticsEnabled === false) {
            return false;
        }

        if (this._initialised) {
            return typeof window.gtag === 'function';
        }

        this._initialised = true;

        if (typeof window.gtag === 'function') {
            return true;
        }

        window.dataLayer = window.dataLayer || [];
        window.gtag = function () { window.dataLayer.push(arguments); };
        window.gtag('js', new Date());
        window.gtag('config', GA_MEASUREMENT_ID);

        const script = this._document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
        this._document.head.appendChild(script);

        return true;
    }

    /**
     * Send a page view to Google Analytics (for SPA route changes).
     * Safe to call on server (no-op).
     * Only sends if analytics is enabled in environment configuration.
     */
    pageView(pagePath: string, pageTitle?: string): void {
        if (!this.ensureTag()) {
            return;
        }

        // The `config` command in the index.html snippet already reported the page
        // the browser was served, so the first router notification is a duplicate
        // of it — unless the user navigated away before this service woke up.
        if (!this._initialPageViewHandled) {
            this._initialPageViewHandled = true;
            const initialPath = window.__spfInitialPagePath;
            if (!initialPath || initialPath === pagePath) {
                return;
            }
        }

        window.gtag('event', 'page_view', {
            page_path: pagePath,
            page_location: this.absoluteUrl(pagePath),
            page_title: pageTitle ?? this._document.title,
        });
    }

    /**
     * Send a custom event to Google Analytics.
     * Safe to call on server (no-op).
     * Only sends if analytics is enabled in environment configuration.
     */
    event(eventName: string, params?: Record<string, unknown>): void {
        if (!this.ensureTag()) {
            return;
        }

        window.gtag('event', eventName, params ?? {});
    }

    private absoluteUrl(pagePath: string): string {
        try {
            return new URL(pagePath, window.location.origin).href;
        } catch {
            return window.location.href;
        }
    }
}
